import {
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from "react";
import type { Components } from "react-markdown";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import AnswerRecap from "@/components/storyboard/AnswerRecap";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import InputPrivacyNotice, {
  NO_PERSONAL_DATA_HINT,
} from "@/components/storyboard/InputPrivacyNotice";
import PrivateAnswersDisclaimer from "@/components/storyboard/PrivateAnswersDisclaimer";
import TaskInstructionHint from "@/components/storyboard/TaskInstructionHint";
import QuizEntryCounter from "@/components/storyboard/quiz/QuizEntryCounter";
import { Textarea } from "@/components/ui/textarea";
import type { TextSelfReflectionQuestionsData } from "@/types/storyboard";
import type {
  SelfReflectionAnswer,
  SelfReflectionAnswerRecord,
} from "@/lib/selfReflectionAnswers";
import { Button } from "@/components/ui/button";

const HINT_TEXT =
  "Es gibt kein Richtig oder Falsch. Deine Antwort wird individuell ausgewertet und du erhältst ermutigendes Feedback.";

export interface SelfReflectionAnswerState {
  value: string;
  submitted: boolean;
  feedback: string;
}

export interface SelfReflectionRunnerState {
  currentIndex: number;
  answers: Record<string, SelfReflectionAnswerState>;
}

export interface SelfReflectionRunnerProps {
  data: TextSelfReflectionQuestionsData;
  onValidate: (
    answer: string,
    index: number
  ) => Promise<{ correct: boolean | null; feedback: string }>;
  onComplete?: (record: SelfReflectionAnswerRecord) => void;
  onPersist?: (record: SelfReflectionAnswerRecord) => void;
  initialPartial?: SelfReflectionRunnerState;
  onPartialChange?: (partial: SelfReflectionRunnerState, index: number) => void;
  readOnly?: boolean;
  reviewRecord?: SelfReflectionAnswerRecord;
}

const emptyState = (): SelfReflectionRunnerState => ({
  currentIndex: 0,
  answers: {},
});

const toRecord = (
  answers: Record<string, SelfReflectionAnswerState>
): SelfReflectionAnswerRecord => {
  const persisted: Record<string, SelfReflectionAnswer> = {};
  for (const [key, answer] of Object.entries(answers)) {
    if (answer.submitted) {
      persisted[key] = { value: answer.value, feedback: answer.feedback };
    }
  }
  return { answers: persisted };
};

const SelfReflectionRunner = ({
  data,
  onValidate,
  onComplete,
  onPersist,
  initialPartial,
  onPartialChange,
  readOnly,
  reviewRecord,
}: SelfReflectionRunnerProps) => {
  const questions = data.questions ?? [];

  const [state, setState] = useState<SelfReflectionRunnerState>(
    () => initialPartial ?? emptyState()
  );

  const hasInteractedRef = useRef<boolean>(initialPartial != null);
  const completedRef = useRef<boolean>(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (readOnly || !hasInteractedRef.current) return;
    onPartialChange?.(state, state.currentIndex);
  }, [state, onPartialChange, readOnly]);

  if (readOnly) {
    const record = reviewRecord ?? { answers: {} };
    return (
      <div className="space-y-6">
        <IntroBlock data={data} />
        <PrivateAnswersDisclaimer className="pl-3" />
        <div className="space-y-4">
          {questions.map((question, index) => (
            <ReviewQuestionCard
              key={index}
              index={index}
              total={questions.length}
              question={question.text}
              answer={record.answers[String(index)]}
            />
          ))}
        </div>
      </div>
    );
  }

  const currentQuestion = questions[state.currentIndex];
  const currentKey = String(state.currentIndex);
  const currentAnswer = state.answers[currentKey];
  const isLast = state.currentIndex === questions.length - 1;

  const finish = (answers: Record<string, SelfReflectionAnswerState>) => {
    if (completedRef.current) return;
    completedRef.current = true;
    const record = toRecord(answers);
    onPersist?.(record);
    onComplete?.(record);
  };

  const handleChange = (value: string) => {
    hasInteractedRef.current = true;
    setState((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [currentKey]: {
          value,
          submitted: prev.answers[currentKey]?.submitted ?? false,
          feedback: prev.answers[currentKey]?.feedback ?? "",
        },
      },
    }));
  };

  const handleSubmit = async () => {
    if (!currentQuestion) return;
    const value = state.answers[currentKey]?.value ?? "";
    if (!value.trim()) return;

    setError(null);
    setIsValidating(true);
    try {
      const result = await onValidate(value, state.currentIndex);
      hasInteractedRef.current = true;
      const submittedAnswer: SelfReflectionAnswerState = {
        value,
        submitted: true,
        feedback: result.feedback,
      };
      setState((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          [currentKey]: submittedAnswer,
        },
      }));
      if (isLast) {
        finish({
          ...state.answers,
          [currentKey]: submittedAnswer,
        });
      }
    } catch {
      setError(
        "Die Auswertung hat nicht geklappt. Bitte versuche es gleich noch einmal."
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleNext = () => {
    setState((prev) => ({ ...prev, currentIndex: prev.currentIndex + 1 }));
  };

  const submitted = currentAnswer?.submitted ?? false;
  const value = currentAnswer?.value ?? "";
  const submitLabel = isLast ? "Abschließen" : "Antwort überprüfen";
  const showRunnerActions = !(submitted && isLast);

  return (
    <div className="flex min-h-full flex-col gap-6">
      <div className="space-y-6">
        <IntroBlock data={data} />

        {currentQuestion && (
          <QuestionStep
            index={state.currentIndex}
            total={questions.length}
            question={currentQuestion.text}
            answer={currentAnswer}
            onChange={handleChange}
            isValidating={isValidating}
          />
        )}
      </div>

      {currentQuestion && showRunnerActions && (
        <div className="mt-auto space-y-3">
          {error && (
            <p className="text-center font-semibold text-error-foreground">
              {error}
            </p>
          )}
          <div className="mx-auto flex w-4/5 justify-center">
            {!submitted ? (
              <Button
                size="xl"
                onClick={handleSubmit}
                disabled={!value.trim() || isValidating}
                className={
                  isValidating
                    ? "opacity-80 animate-pulse cursor-wait"
                    : "disabled:opacity-60"
                }
              >
                {isValidating ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="animate-spin w-5 h-5 shrink-0" />
                    <span>Wird ausgewertet…</span>
                  </span>
                ) : (
                  submitLabel
                )}
              </Button>
            ) : (
              <Button size="xl" onClick={handleNext}>
                Nächste Frage
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelfReflectionRunner;

const IntroBlock = ({ data }: { data: TextSelfReflectionQuestionsData }) => {
  if (!data.introduction) return null;

  return (
    <div className="prose max-w-full pl-3 text-primary prose-p:my-2">
      <CourseMarkdown>{data.introduction}</CourseMarkdown>
    </div>
  );
};

const QuestionText = ({ question }: { question: string }) => (
  <div className="prose quiz-question max-w-full font-semibold text-primary">
    <CourseMarkdown>{question}</CourseMarkdown>
  </div>
);

const QuestionWithCounter = ({
  question,
  index,
  total,
}: {
  question: string;
  index: number;
  total: number;
}) => (
  <QuizEntryCounter currentIndex={index} total={total}>
    <QuestionText question={question} />
  </QuizEntryCounter>
);

/** LLMs often wrap unstructured coaching copy in a single markdown fence. */
const unwrapWrappingMarkdownFence = (markdown: string): string => {
  const trimmed = markdown.trim();
  const match = trimmed.match(/^```[a-zA-Z0-9_+-]*\r?\n([\s\S]*?)\r?\n```$/);
  return match ? match[1] : markdown;
};

// Keep coaching copy on the accent surface: typography's `prose` paints
// <pre>/<code> as a dark terminal block, which is for real code examples.
const coachingFeedbackMarkdown: Components = {
  pre: ({ children }: ComponentPropsWithoutRef<"pre">) => (
    <div className="whitespace-pre-wrap">{children}</div>
  ),
  code: ({ children }: ComponentPropsWithoutRef<"code">) => (
    <span className="whitespace-pre-wrap">{children}</span>
  ),
};

const FeedbackBlock = ({ feedback }: { feedback: string }) => {
  if (!feedback) return null;
  return (
    <div className="w-full rounded-lg border border-accent bg-accent p-4">
      <p className="mb-2 font-bold uppercase tracking-wide text-muted-foreground">
        Feedback
      </p>
      <div
        className={cn(
          "prose prose-sm max-w-full text-primary",
          "prose-p:text-inherit prose-strong:text-inherit prose-li:text-inherit",
          "prose-pre:bg-transparent prose-pre:text-inherit prose-pre:font-sans",
          "prose-pre:m-0 prose-pre:p-0 prose-pre:overflow-visible prose-pre:whitespace-pre-wrap",
          "prose-code:bg-transparent prose-code:text-inherit prose-code:font-sans"
        )}
      >
        <CourseMarkdown components={coachingFeedbackMarkdown}>
          {unwrapWrappingMarkdownFence(feedback)}
        </CourseMarkdown>
      </div>
    </div>
  );
};

interface QuestionStepProps {
  index: number;
  total: number;
  question: string;
  answer?: SelfReflectionAnswerState;
  onChange: (value: string) => void;
  isValidating: boolean;
}

const QuestionStep = ({
  index,
  total,
  question,
  answer,
  onChange,
  isValidating,
}: QuestionStepProps) => {
  const privacyNoticeId = useId();
  const submitted = answer?.submitted ?? false;
  const value = answer?.value ?? "";

  const renderAnswerField = () => {
    if (submitted) {
      return (
        <div className="space-y-4">
          <AnswerRecap value={value} />
          <FeedbackBlock feedback={answer?.feedback ?? ""} />
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {/* sr-only: same privacy copy as the placeholder, linked via
            aria-describedby so screen readers keep the warning after typing. */}
        <InputPrivacyNotice id={privacyNoticeId} className="sr-only" />
        <Textarea
          rows={10}
          value={value}
          placeholder={NO_PERSONAL_DATA_HINT}
          onChange={(e) => onChange(e.target.value)}
          disabled={isValidating}
          className="w-full rounded-lg border-2 border-primary bg-background shadow-sm resize-none transition-colors field-sizing-fixed focus-visible:border-primary focus-visible:ring-primary/40"
          aria-label={`Antwort auf Frage ${index + 1}`}
          aria-describedby={privacyNoticeId}
        />
        <PrivateAnswersDisclaimer className="pl-3 text-left" />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <QuestionWithCounter question={question} index={index} total={total} />

      {!submitted && <TaskInstructionHint>{HINT_TEXT}</TaskInstructionHint>}

      {renderAnswerField()}
    </div>
  );
};

interface ReviewQuestionCardProps {
  index: number;
  total: number;
  question: string;
  answer?: SelfReflectionAnswer;
}

const ReviewQuestionCard = ({
  index,
  total,
  question,
  answer,
}: ReviewQuestionCardProps) => (
  <div
    data-slot="quiz-review-question"
    className="space-y-4 rounded-2xl border border-border bg-card py-5 pl-3 pr-5"
  >
    <QuestionWithCounter question={question} index={index} total={total} />

    <AnswerRecap value={answer?.value ?? ""} />
    <FeedbackBlock feedback={answer?.feedback ?? ""} />
  </div>
);
